import {
  Component, Input, forwardRef, ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Single-select dropdown styled to match the trends dashboard filter panels
 * (pill trigger + chevron, white rounded panel, hover rows, green selected
 * state, optional search). Implements ControlValueAccessor so it slots into
 * reactive forms via `formControlName`, replacing native <select>.
 */
@Component({
  selector: 'sp-select-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './select-dropdown.component.html',
  styleUrls: ['./select-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectDropdownComponent),
      multi: true,
    },
  ],
})
export class SelectDropdownComponent implements ControlValueAccessor {
  /** Accepts string[] or {value,label}[] (readonly tuples ok); normalized to SelectOption[]. */
  @Input() set options(v: readonly (SelectOption | string)[] | null) {
    this._options = (v ?? []).map(o =>
      typeof o === 'string' ? { value: o, label: o } : o,
    );
  }
  get options(): SelectOption[] { return this._options; }
  private _options: SelectOption[] = [];

  @Input() placeholder       = 'Select…';
  @Input() searchable        = false;
  @Input() searchPlaceholder = 'Search';
  /** Adds the error border (e.g. invalid && touched). */
  @Input() invalid = false;

  open       = false;
  disabled   = false;
  value: string | null = null;
  searchTerm = '';

  private onChange:  (v: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  get selectedLabel(): string {
    return this._options.find(o => o.value === this.value)?.label ?? '';
  }

  get filteredOptions(): SelectOption[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this._options;
    return this._options.filter(o => o.label.toLowerCase().includes(term));
  }

  toggle(): void {
    if (this.disabled) return;
    this.open = !this.open;
    if (this.open) this.searchTerm = '';
    else this.onTouched();
    this.cdr.markForCheck();
  }

  close(): void {
    if (!this.open) return;
    this.open = false;
    this.onTouched();
    this.cdr.markForCheck();
  }

  select(opt: SelectOption): void {
    this.value = opt.value;
    this.onChange(this.value);
    this.open = false;
    this.onTouched();
    this.cdr.markForCheck();
  }

  // ── ControlValueAccessor ──
  writeValue(v: string | null): void {
    this.value = v ?? null;
    this.cdr.markForCheck();
  }
  registerOnChange(fn: (v: string | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void {
    this.disabled = d;
    this.cdr.markForCheck();
  }
}
